import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Switch,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, GlobeIcon, PlusCircleIcon, SearchIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type {
  ApiResponseOrganizationDomain,
  ApiResponseOrganizationDomainList,
} from '../../../types/api/ApiResponseOrganizationDomain';
import type { OrganizationDomainListItem } from '../../../types/models';
import { formatDate } from '../../../utils/dateFormat';
import { renderDomainStatus } from './domainStatus';

const AUTO_ADOPT_TOOLTIP =
  'When enabled, new users signing in for the first time with an email at this verified domain will automatically join your organization.';

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;

function validateDomainName(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  if (!v) return 'Domain name is required';
  if (v.length > 253) return 'Domain name is too long (max 253 characters)';
  if (!DOMAIN_REGEX.test(v)) return 'Enter a valid domain name (e.g. example.com)';
  return null;
}

const Domains: React.FC = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [domains, setDomains] = useState<OrganizationDomainListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const [pendingAutoAdoptIds, setPendingAutoAdoptIds] = useState<ReadonlySet<number>>(() => new Set());

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      setError(null);

      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await apiFetch('/organization/domains', {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponseOrganizationDomainList = await response.json();
      setDomains(data.data.domains);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching domains:', err);
      setError(err instanceof Error ? err.message : 'Failed to load domains. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    void fetchDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  const filteredDomains = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return domains;
    return domains.filter((d) => d.name.toLowerCase().includes(q));
  }, [domains, debouncedSearch]);

  const paginatedDomains = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredDomains.slice(start, start + perPage);
  }, [filteredDomains, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleAutoAdoptToggle = async (domain: OrganizationDomainListItem, nextValue: boolean) => {
    if (domain.status !== 'verified') return;
    const id = domain.id;

    setPendingAutoAdoptIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      const response = await apiFetch(`/organization/domains/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ auto_adopt: nextValue }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const message = (body as { message?: string } | null)?.message ?? `HTTP error! status: ${response.status}`;
        throw new Error(message);
      }

      const data: ApiResponseOrganizationDomain = await response.json();
      const updated = data.data.domain;
      setDomains((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                auto_adopt: updated.auto_adopt,
                status: updated.status,
                verified_at: updated.verified_at,
              }
            : d,
        ),
      );

      addNotification({
        title: nextValue ? 'Auto-adopt enabled' : 'Auto-adopt disabled',
        srTitle: 'Success alert',
        description: `Auto-adopt for ${domain.name} is now ${nextValue ? 'on' : 'off'}.`,
        variant: 'success',
      });
      setDrawerOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update auto-adopt';
      addNotification({
        title: 'Could not update auto-adopt',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setPendingAutoAdoptIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const resetCreateForm = () => {
    setCreateName('');
    setCreateError(null);
  };

  const handleModalClose = () => {
    setIsCreateOpen(false);
    resetCreateForm();
  };

  const handleCreateConfirm = () => {
    const validationError = validateDomainName(createName);
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    const normalized = createName.trim().toLowerCase();

    setIsCreateOpen(false);
    resetCreateForm();

    navigate('/organization/domains/new', {
      state: { pendingName: normalized },
    });
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load domains"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void fetchDomains()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (domains.length === 0) {
      return (
        <EmptyState titleText="No domains registered" icon={GlobeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Register your first domain to start using verified domains across mail, DNS, certificates, and VM FQDNs.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create domain
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by domain name..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredDomains.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredDomains.length === 0 ? (
          <EmptyState titleText="No domains match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all domains.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Organization domains" variant="compact" borders={false}>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Auto-adopt</Th>
                <Th>Verified at</Th>
                <Th>Created at</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedDomains.map((domain) => {
                const isVerified = domain.status === 'verified';
                const isPending = pendingAutoAdoptIds.has(domain.id);
                return (
                  <Tr key={domain.id}>
                    <Td dataLabel="Name">
                      <Link to={`/organization/domains/${domain.id}`}>{domain.name}</Link>
                    </Td>
                    <Td dataLabel="Status">{renderDomainStatus(domain.status)}</Td>
                    <Td dataLabel="Auto-adopt">
                      <Tooltip content={AUTO_ADOPT_TOOLTIP}>
                        <Switch
                          id={`auto-adopt-${domain.id}`}
                          aria-label="Auto-adopt new users with this verified domain"
                          isChecked={domain.auto_adopt}
                          isDisabled={!isVerified || isPending}
                          onChange={(_event, checked) => void handleAutoAdoptToggle(domain, checked)}
                        />
                      </Tooltip>
                    </Td>
                    <Td dataLabel="Verified at">{formatDate(domain.verified_at)}</Td>
                    <Td dataLabel="Created at">{formatDate(domain.created_at)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredDomains.length > 0 && (
          <Pagination
            itemCount={filteredDomains.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  const showCreateButton = !loading;

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization" isActive>
            Organization
          </BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <GlobeIcon />
              </Icon>
              Domains
            </Content>
            <Content component="p">
              Register and verify the domains your organization owns. Verified domains are reusable across mail, DNS,
              certificates, and VM FQDNs.
            </Content>
          </FlexItem>
          {showCreateButton && (
            <FlexItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create domain
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      <Modal
        variant={ModalVariant.medium}
        isOpen={isCreateOpen}
        onClose={handleModalClose}
        aria-labelledby="create-domain-title"
        aria-describedby="create-domain-description"
      >
        <ModalHeader
          title="Create domain"
          description="Enter the domain name you want to register for your organization."
          labelId="create-domain-title"
          descriptorId="create-domain-description"
        />
        <ModalBody>
          <Form id="create-domain-form" isWidthLimited>
            <FormGroup label="Domain name" isRequired fieldId="create-domain-name">
              <TextInput
                id="create-domain-name"
                name="domain-name"
                type="text"
                autoFocus
                value={createName}
                onChange={(_e, val) => {
                  setCreateName(val);
                  if (createError) setCreateError(null);
                }}
                isRequired
                validated={createError ? 'error' : 'default'}
                placeholder="example.com"
              />
              {createError && (
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem variant="error">{createError}</HelperTextItem>
                  </HelperText>
                </FormHelperText>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={handleCreateConfirm}>
            Confirm
          </Button>
          <Button variant="link" onClick={handleModalClose}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Domains;
